import keyCodes from './lib/keyCodes';

/**
 * MenuItem class for managing menu items' keyboard interactions.
 *
 * @param {HTMLElement} list The list to manage.
 */
export default class MenuItem {
  /**
   * Test for a list as the next sibling element.
   *
   * @param {HTMLElement} element The element for which we're looking for a sibling.
   *
   * @return {HTMLElement|Boolean}
   */
  static nextElementIsUl(element) {
    const next = element.nextElementSibling;
    return (null !== next && 'UL' === next.nodeName) ? next : false;
  }

  /**
   * Start the component
   */
  constructor(list) {
    /**
     * The default string description for this object.
     *
     * @type {string}
     */
    this.stringDescription = 'MenuItem';

    /**
     * The list to manage
     * @type {HTMLElement}
     */
    this.list = list;

    // Save a reference to the MenuItem instance on the list item.
    Object.assign(list, { menuItem: this });

    /**
     * The list's child elements.
     * @type {Array}
     */
    this.listItems = Array.prototype.slice.call(list.children);

    // Bind class methods
    this.handleListKeydown = this.handleListKeydown.bind(this);
    this.destroy = this.destroy.bind(this);

    this.init();
  }

  /**
   * Get the string description for this object.
   * E.x., MenuItem.toString() === '[object MenuItem]'
   *
   * @return {string}
   */
  get [Symbol.toStringTag]() {
    return this.stringDescription;
  }

  /**
   * Collect menu links and recursively instantiate sublist menu items.
   */
  init() {
    // Collect menu items.
    this.menuItems = this.listItems.reduce((acc, item) => {
      const itemLink = item.firstElementChild;

      if (null !== itemLink && 'A' === itemLink.nodeName) {
        return [...acc, itemLink];
      }

      return acc;
    }, []);

    /**
     * The number of menu items.
     * @type {Number}
     */
    this.menuItemsLength = this.menuItems.length;

    // Set menu item link attributes and event listeners.
    this.list.setAttribute('role', 'menu');
    this.menuItems.forEach((link, index) => {
      link.parentElement.setAttribute('role', 'presentation');

      // Add size and position attributes.
      link.setAttribute('role', 'menuitem');
      link.setAttribute('aria-setsize', this.menuItemsLength);
      link.setAttribute('aria-posinset', index + 1);

      link.addEventListener('keydown', this.handleListKeydown);

      const siblingList = this.constructor.nextElementIsUl(link);
      if (siblingList) {
        const subList = new MenuItem(siblingList);
        // Save the list's previous sibling.
        subList.previousSibling = link;
      }
    });

    // Save the menu's first and last items.
    [this.firstItem] = this.menuItems;
    this.lastItem = this.menuItems[this.menuItemsLength - 1];
  }

  /**
   * Handle keydown events on menu items.
   *
   * @param {Object} event The event object.
   */
  handleListKeydown(event) {
    const { keyCode } = event;
    const {
      UP,
      DOWN,
      LEFT,
      RIGHT,
    } = keyCodes;
    const { activeElement } = document;
    const activeDescendant = (this.list.contains(activeElement)
      ? activeElement
      : this.menuItems[0]);
    const menuLastIndex = this.menuItems.length - 1;
    const activeIndex = this.menuItems.indexOf(activeDescendant);

    if ([UP, DOWN].includes(keyCode)) {
      // Move through sibling list items.
      event.stopPropagation();
      event.preventDefault();

      // Determine the direction.
      let nextIndex = (keyCode === UP)
        ? activeIndex - 1
        : activeIndex + 1;

      // Move to the end if we're moving to the previous child from the first child.
      if (UP === keyCode && 0 > nextIndex) {
        nextIndex = menuLastIndex;
      }

      // Move to first child if we're at the end.
      if (DOWN === keyCode && menuLastIndex < nextIndex) {
        nextIndex = 0;
      }

      this.menuItems[nextIndex].focus();
    } else if (RIGHT === keyCode) {
      // Drill down into a nested list, if present.
      const siblingElement = this.constructor.nextElementIsUl(activeDescendant);

      if (
        siblingElement
        && Object.hasOwnProperty.call(siblingElement, 'menuItem')
      ) {
        event.stopPropagation();
        event.preventDefault();

        const { menuItem } = siblingElement;
        menuItem.firstItem.focus();
      }
    } else if (LEFT === keyCode) {
      // Move up to the list's previous sibling, if present.
      if (undefined !== this.previousSibling) {
        event.stopPropagation();
        event.preventDefault();

        // Close the popup if it exists and is exapnded.
        if ('[object Popup]' === this.previousSibling?.popup?.toString()) {
          event.stopPropagation();
          event.preventDefault();

          const { popup } = this.previousSibling;
          if (popup.state.expanded) {
            popup.setExpandedState(false);
          }
        }

        this.previousSibling.focus();
      }
    }
  }

  /**
   * Destroy the MenuItem (recursively)
   */
  destroy() {
    if (undefined !== this.list.menuItem) {
      // Set the menuItem property to null
      this.list.menuItem = null;

      this.list.removeAttribute('role');

      this.menuItems.forEach((link) => {
        link.parentElement.removeAttribute('role');

        // Remove size and position attributes.
        link.removeAttribute('role');
        link.removeAttribute('aria-setsize');
        link.removeAttribute('aria-posinset');

        link.removeEventListener('keydown', this.handleListKeydown);

        // Destroy nested MenuItems.
        const siblingList = this.constructor.nextElementIsUl(link);
        if (
          siblingList
          && undefined !== siblingList.menuItem
          && '[object MenuItem]' === siblingList?.menuItem?.toString()
        ) {
          siblingList.menuItem.destroy();
        }
      });
    }
  }
}
